import { createRoot } from 'react-dom/client'

import { getRawRoute, getRouteState } from '../common/frontend/route-utils.js'
import { initConsoleBuffer } from '../common/frontend/utils/console-buffer.js'
import { ApplicationId, ApplicationRole } from '../common/types/common.js'
import { createApi } from './api.js'
import { FeedbackWidget } from './components/feedback-widget/feedback-widget.js'
import { IntegrationsGate } from './components/integrations-gate/integrations-gate.js'
import { buildHeroData } from './hero-data.js'
import './index.scss'
import { routes } from './routes.js'
import { State, View } from './types.js'
import { loadInitialLanguage } from './utils/language.js'
import {
    getEmptyDataState,
    loadPortalRoleIfNeeded,
    refreshActiveCampaign,
} from './utils/load-utils.js'
import { applyTheme, loadInitialTheme } from './utils/theme.js'
import { AppShell } from './views/app-shell/app-shell.js'
import { LoginScreen } from './views/login/login.js'

initConsoleBuffer()

// eslint-disable-next-line @typescript-eslint/no-misused-promises
window.addEventListener('load', async () => {
    const state: State = {
        isAuthenticated: true, // Assume authenticated until proven otherwise
        route: getRouteState(routes),
        lang: loadInitialLanguage(),
        themePreference: loadInitialTheme(),
        notifications: [],
        eat: {
            stats: getEmptyDataState(),
            orders: getEmptyDataState(),
            meals: getEmptyDataState(),
            settings: getEmptyDataState(),
            cart: [],
            ordersPage: 1,
            selectedMealId: null,
            selectedCommentId: null,
            isOrdering: false,
            isPaying: false,
        },
        signatures: getEmptyDataState(),
        me: getEmptyDataState(),
        portalRole: getEmptyDataState(),
        playtime: {
            profile: getEmptyDataState(),
            leaderboard: getEmptyDataState(),
        },
        balancetime: {
            authStatus: null,
            data: null,
            isLoading: true,
            loadError: null,
            rangeType: 'month',
            offset: 0,
            selectedUser: '',
        },
        myLeaveBalance: getEmptyDataState(),
        hasActiveCampaign: false,
        activeCampaignAnswered: false,
        portalLinks: getEmptyDataState(),
        worktime: {
            projects: getEmptyDataState(),
            activeTab: 'mine',
            searchQuery: '',
            selectedProject: null,
        },
        urlShortener: {
            links: getEmptyDataState(),
            detail: getEmptyDataState(),
            accessLogs: getEmptyDataState(),
            currentLinkId: null,
            accessLogsPage: 1,
            isCreating: false,
            isSavingQr: false,
            isDeleting: false,
            showCreateForm: false,
        },
    }

    // Inline-skript index.html-is tegi sama enne CSS-i; see uuendab ka meta theme-color'i.
    applyTheme(state.themePreference)

    const container = document.querySelector('#root')!
    const root = createRoot(container)

    const renderApp = () => {
        document.title = 'Hub'

        if (!state.isAuthenticated) {
            const loginError = new URLSearchParams(window.location.search).get('error')
            root.render(<LoginScreen lang={state.lang} error={loginError ?? undefined} />)
            return
        }

        const { route } = state
        const activeView = route.definition.render(view, route.params)
        // Build hero data here (outside React render) — it calls loadXIfNeeded,
        // which can trigger view.update(); that must not happen during a render.
        const heroData = buildHeroData(view)
        // Load PH role here too (same reason): loadPortalRoleIfNeeded triggers
        // view.update() and must run outside the React render.
        const portalRoleData = loadPortalRoleIfNeeded(view).data
        root.render(
            <>
                <AppShell view={view} heroData={heroData} portalRole={portalRoleData}>
                    {activeView}
                </AppShell>
                <FeedbackWidget view={view} />
                {/* Post-login nudge to connect personal integrations (skippable) */}
                <IntegrationsGate lang={state.lang} />
            </>,
        )
    }

    // Wire up auth change callback so http-utils can trigger login screen
    state._onAuthChange = renderApp

    const view: View = {
        state,
        update: renderApp,
        api: createApi(state),
    }

    // Check session before rendering
    try {
        const res = await fetch('/api/session')
        const session = (await res.json()) as {
            isLoggedIn: boolean
            id?: number
            name?: string
            role?: string
            applicationRoles?: Record<ApplicationId, ApplicationRole>
            isManager?: boolean
        }

        if (!session.isLoggedIn) {
            state.isAuthenticated = false
            renderApp()
            return
        }

        if (session.id && session.name) {
            state.user = {
                id: session.id,
                name: session.name,
                role: session.role ?? 'user',
                applicationRoles: session.applicationRoles ?? {
                    eat: 'user',
                    playtime: 'user',

                    q12: 'user',
                    portal: 'user',
                    url_shortener: 'user',
                },
                isManager: session.isManager,
            }
        }
    } catch {
        // If session check fails, show login
        state.isAuthenticated = false
        renderApp()
        return
    }

    // Register the popstate listener BEFORE the initial onEnter so that
    // navigateTo() calls from onEnter (e.g. the /balancetime → /me/balancetime
    // redirect preserving the Harvest OAuth ?code= param) are properly routed.
    let lastSearch = window.location.search
    window.addEventListener('popstate', () => {
        const raw = getRawRoute()

        if (raw !== state.route.raw) {
            state.route.definition.onLeave?.(view, state.route.params)
            state.route = getRouteState(routes)
            state.route.definition.onEnter?.(view, state.route.params)
            renderApp()
        } else if (window.location.search !== lastSearch) {
            // Same route, but the query string changed (e.g. profile ?tab=).
            renderApp()
        }

        lastSearch = window.location.search
    })

    // Authenticated — render the app
    state.route.definition.onEnter?.(view, state.route.params)
    renderApp()

    // Check for an open Q12 campaign (async, re-renders when done)
    void refreshActiveCampaign(view)
})
